<template>
  <div class="pre-projects-data-table">
    <BasePullToRefresh v-if="isTouchScreen && !trialRestricted" :pending="pending" @refresh="$emit('refresh')" />
    <!-- Hidden on trial: no result count, column toggle or refresh — the
         preview is a fixed first page. -->
    <div v-if="!trialRestricted" class="pre-projects-data-table__results-section">
      <span class="pre-projects-data-table__count">
        <span class="number-monospace">{{ formatLocalizedNumber(totalCount) }}</span>
        {{ ' ' + $tc('page.pre-projects.results', totalCount) }}
      </span>
      <div class="pre-projects-data-table__buttons">
        <BasePhoneListControls v-if="isPhone && hasResults" :selectable="false" @sort="phoneSortOpen = true" />
        <!-- On phones, in a sheet (docs/page-layout.md). -->
        <v-menu offset-y left :close-on-content-click="false" transition="scale-transition" :disabled="isPhone">
          <template v-slot:activator="{ on, attrs }">
            <span v-on="on" v-bind="attrs" @click="openYearsSheet">
              <BaseRoundButton
                icon="sliders"
                :size="36"
                is-white
                :tooltip-text="$t('page.pre-projects.toggle-columns')"
              />
            </span>
          </template>
          <div class="pre-projects-data-table__columns-menu">
            <div class="pre-projects-data-table__columns-menu-heading">
              {{ $t('page.pre-projects.toggle-columns') }}
            </div>
            <FormCheckbox
              v-for="year in availableYears"
              :key="year"
              :value="selectedYears.includes(year)"
              :label="$t('page.pre-projects.data-table-titles.budget', { year })"
              size="small"
              @input="toggleYear(year)"
            />
          </div>
        </v-menu>
        <BaseBottomSheet v-if="isPhone" v-model="yearsSheetOpen" :title="$t('page.pre-projects.budget-years')">
          <BaseSheetChecklist
            :value="selectedYears"
            :choices="yearChoices"
            :label="$t('page.pre-projects.budget-years')"
            :search-from="Infinity"
            @input="selectedYears = $event"
            @done="yearsSheetOpen = false"
          />
        </BaseBottomSheet>
        <BaseRefreshButton :pending="pending" @refresh="$emit('refresh')" />
      </div>
    </div>

    <div v-if="hasResults" class="pre-projects-data-table__content">
      <PreProjectsPhoneList
        v-if="isPhone"
        :pre-projects="items"
        :budgets-of="phoneBudgets"
        :location-label="locationLabel"
        :share-pending="isSharePending"
        :trial-restricted="trialRestricted"
        :sort-open.sync="phoneSortOpen"
        :sorting-field="sortingField"
        :sorting-order="sortingOrder"
        :has-more="!trialRestricted && page < totalPages"
        :loading-more="loadingMore"
        :load-more-error="loadMoreError"
        :pending="pending"
        @load-more="$emit('load-more')"
        @sort="onPhoneSort"
        @follow="onFollowClick"
        @share="onShareClick"
        @edit="(item) => $emit('edit', item)"
        @delete="(item) => $emit('delete', item)"
        @open-source="openSource"
      />
      <TableNormal
        v-else
        :headers="headers"
        :items="items"
        :items-per-page="-1"
        hide-default-footer
        item-key="id"
        @update:sort-by="onSortBy"
        @update:sort-desc="onSortDesc"
      >
        <template v-slot:item.title="{ item }">
          <div class="pre-projects-data-table__row-title-container">
            <span class="pre-projects-data-table__row-title">
              {{ item.title }}
              <BaseTag
                v-if="isNewPreProject(item)"
                class="pre-projects-data-table__new-tag"
                tone="info"
                preset="primary"
              >
                {{ $t('tags.new') }}
              </BaseTag>
            </span>
            <div class="pre-projects-data-table__row-subtitle">
              <BaseTag
                v-if="hasProjectNumber(item)"
                v-tooltip="{ content: $t('page.pre-projects.form.project-number-label'), placement: 'top' }"
                tone="neutral"
                preset="soft"
              >
                #{{ item.projectNumber }}
              </BaseTag>
              <!-- Source: no tenantId = imported by Aloha, otherwise entered manually by the tenant. -->
              <BaseTag v-if="item.tenantId" preset="soft" tone="info" icon="user">
                {{ $t('page.pre-projects.source.manual') }}
              </BaseTag>
              <!-- A single source opens directly from the tag; several show a
                   count whose hover lists them all. -->
              <BaseTag
                v-if="allSources(item).length === 1"
                class="pre-projects-data-table__sources-tag"
                preset="soft"
                tone="neutral"
                @click.native.stop="openSource(item, allSources(item)[0])"
              >
                {{ $tc('page.pre-projects.sources-tag', 1, { count: 1 }) }}
                <BaseIcon name="external-link" class="pre-projects-data-table__sources-tag-ico" />
              </BaseTag>
              <v-popover v-else-if="allSources(item).length" trigger="hover" placement="top">
                <BaseTag class="pre-projects-data-table__sources-tag" preset="soft" tone="neutral">
                  {{
                    $tc('page.pre-projects.sources-tag', allSources(item).length, { count: allSources(item).length })
                  }}
                </BaseTag>
                <template v-slot:popover>
                  <div class="pre-projects-data-table__sources">
                    <button
                      v-for="source in allSources(item)"
                      :key="source.id"
                      type="button"
                      class="pre-projects-data-table__source"
                      @click="openSource(item, source)"
                    >
                      <BaseIcon
                        :name="source.type === 'LINK' ? 'link' : 'file-text'"
                        class="pre-projects-data-table__source-ico"
                      />
                      <span class="pre-projects-data-table__source-name">{{ source.title || source.url }}</span>
                      <BaseIcon name="external-link" class="pre-projects-data-table__source-open" />
                    </button>
                  </div>
                </template>
              </v-popover>
            </div>
          </div>
        </template>

        <template v-slot:item.emitter="{ item }">
          <!-- The backend's display value: the stored emitter, else the region (else
               province) of one of its locations. `emitter` stays the stored value so
               the edit form never persists that fallback. -->
          <span v-if="item.displayEmitter" class="pre-projects-data-table__emitter">{{ item.displayEmitter }}</span>
          <span v-else class="pre-projects-data-table__empty-cell">—</span>
        </template>

        <!-- Locations: a pre-project applies to one or more places (a city, a
             custom place, a whole region or province). Each is one line,
             "Name · Region" like the form's chips, the region muted; the
             province shows on hover (none when the entry IS the province).
             Past the first few, a "+N" tag — the only tag in the cell, so it
             can't be mistaken for a place — lists the rest on hover. -->
        <template v-slot:item.city="{ item }">
          <div class="pre-projects-data-table__locations">
            <span v-if="!locationsOf(item).length" class="pre-projects-data-table__empty-cell">—</span>
            <div
              v-for="(location, index) in visibleLocations(item)"
              :key="`${item.id}-location-${index}`"
              v-tooltip="{ content: locationProvince(location), placement: 'top' }"
              class="pre-projects-data-table__location"
            >
              <span class="pre-projects-data-table__location-name">{{ locationName(location) }}</span>
              <span v-if="locationRegion(location)" class="pre-projects-data-table__location-region">
                · {{ locationRegion(location) }}
              </span>
            </div>
            <BaseTag
              v-if="hiddenLocations(item).length"
              v-tooltip="{ content: hiddenLocationsTooltip(item), placement: 'top', classes: 'multiline' }"
              class="pre-projects-data-table__locations-more"
              tone="neutral"
              preset="soft"
            >
              +{{ hiddenLocations(item).length }}
            </BaseTag>
          </div>
        </template>

        <template v-for="year in visibleYears" v-slot:[`item.budget-${year}`]="{ item }">
          <div :key="year" class="pre-projects-data-table__budget">
            <div class="pre-projects-data-table__budget-main">
              <!-- Budget aligned exactly to a sub-year period: Q1–Q4 or S1/S2. -->
              <BaseTag
                v-if="budgetPeriodLabel(item, year)"
                v-tooltip="{ content: budgetPeriodTooltip(item, year), placement: 'top' }"
                preset="soft"
                tone="neutral"
              >
                {{ budgetPeriodLabel(item, year) }}
              </BaseTag>
              <!-- Custom span (exact dates): a calendar chip carrying the range. -->
              <BaseTag
                v-else-if="budgetExactEntry(item, year)"
                v-tooltip="{ content: budgetExactTooltip(item, year), placement: 'top' }"
                class="pre-projects-data-table__budget-exact"
                preset="soft"
                tone="neutral"
                icon="calendar"
              />
              <span
                v-if="showsBudgetAmount(item, year)"
                class="pre-projects-data-table__budget-amount"
                :class="{ 'is-na': !hasBudgetAmount(item, year) }"
              >
                {{ budgetLabel(item, year) }}
              </span>
              <!-- Later years of a multi-year custom budget reference its start
                   (e.g. "S2-2026") instead of repeating the amount. -->
              <BaseTag
                v-for="origin in budgetOrigins(item, year)"
                :key="`${year}-${origin.label}`"
                v-tooltip="{ content: origin.tooltip, placement: 'top' }"
                preset="soft"
                tone="neutral"
              >
                {{ origin.label }}
              </BaseTag>
            </div>
            <!-- Whole-years budgets keep the classic span under the amount. -->
            <span v-if="budgetSpanLabel(item, year)" class="pre-projects-data-table__budget-span">
              {{ budgetSpanLabel(item, year) }}
            </span>
          </div>
        </template>

        <template v-slot:item.actions="{ item }">
          <div class="pre-projects-data-table__actions">
            <!-- Custom (tenant-entered) pre-projects can be edited and deleted. -->
            <template v-if="item.tenantId">
              <BaseRoundButton
                is-neutral
                :size="35"
                :tooltip-text="$t('actions.edit')"
                icon="edit-2"
                is-white
                @click.native.stop="$emit('edit', item)"
              />
              <BaseRoundButton
                is-neutral
                :size="35"
                :tooltip-text="$t('actions.delete')"
                icon="trash-2"
                is-white
                @click.native.stop="$emit('delete', item)"
              />
            </template>
            <!-- Follow (bookmark) and share are hidden on trial — watchlists and
                 sharing come with the subscription (the API refuses both). -->
            <BaseRoundButton
              v-if="!trialRestricted"
              :is-light="item.followed"
              :is-neutral="!item.followed"
              :size="35"
              :tooltip-text="item.followed ? $t('actions.unfollow') : $t('actions.follow')"
              icon="bookmark"
              is-white
              @click.native.stop="onFollowClick(item)"
            />
            <BaseRoundButton
              v-if="!trialRestricted"
              :is-light="item.shared"
              :is-neutral="!item.shared"
              :size="35"
              :tooltip-text="item.shared ? $t('actions.unshare') : $t('actions.share')"
              :tooltip-subtitle="$t('page.pre-projects.share-tooltip-subtitle')"
              :disabled="isSharePending(item.id)"
              icon="share-2"
              is-white
              @click.native.stop="onShareClick(item)"
            />
          </div>
        </template>
      </TableNormal>

      <!-- Trial preview: only the first page — pagination stays hidden and the
           end-of-results banner counts what a subscription would unlock. -->
      <BaseInfoBanner
        v-if="trialRestricted && trialRemainingCount > 0"
        class="pre-projects-data-table__trial-end-banner"
        tone="warning"
        preset="soft"
        :title="
          $t('upsell.pre-projects.trial-end-title', {
            shown: formatLocalizedNumber(items.length),
            total: formatLocalizedNumber(totalCount),
          })
        "
        :description="
          $t('upsell.pre-projects.trial-end-description', { count: formatLocalizedNumber(trialRemainingCount) })
        "
      >
        <template #icon>
          <BaseIcon name="lock" />
        </template>
      </BaseInfoBanner>

      <div v-if="totalPages > 1 && !trialRestricted && !isPhone" class="pre-projects-data-table__pagination">
        <TablePagination v-model="currentPage" :length="totalPages" :total-visible="10" @input="onInputPage" />
      </div>
    </div>

    <TableState
      v-if="!hasResults"
      :state-loading="pending"
      :state-no-data="!pending"
      :state-text="pending ? $t('page.pre-projects.loading') : $t('page.pre-projects.no-data')"
    />
  </div>
</template>

<script>
import BaseBottomSheet from '@/components/base/BaseBottomSheet.vue';
import BaseIcon from '@/components/base/BaseIcon.vue';
import BaseInfoBanner from '@/components/base/BaseInfoBanner.vue';
import BasePhoneListControls from '@/components/base/BasePhoneListControls.vue';
import BasePullToRefresh from '@/components/base/BasePullToRefresh.vue';
import BaseRefreshButton from '@/components/base/BaseRefreshButton.vue';
import BaseRoundButton from '@/components/base/BaseRoundButton.vue';
import BaseSheetChecklist from '@/components/base/BaseSheetChecklist.vue';
import BaseTag from '@/components/base/BaseTag.vue';
import FormCheckbox from '@/components/form/FormCheckbox.vue';
import PreProjectsPhoneList from '@/components/page-pre-projects/PreProjectsPhoneList.vue';
import TableNormal from '@/components/table/TableNormal.vue';
import TablePagination from '@/components/table/TablePagination.vue';
import TableState from '@/components/table/TableState.vue';
import { escapeHtml } from '@/util/html-util';

import { preProjectSourcesService } from '@/services/pre-project-sources.service';
import { formatNumToCurrency } from '@/util/currency-util';
import { formatStandardDate } from '@/util/date-util';
import { LOCATION_LEVEL, locationDisplayName, locationsOf } from '@/util/location-util';
import { formatLocalizedNumber } from '@/util/number-util';
import { hasProjectNumber, isNewPreProject } from '@/util/pre-project-util';
import { getSortingOrder } from '@/util/sorting-util';
import { viewport } from '@/util/viewport';
import { mapActions, mapGetters } from 'vuex';

// The API's costsByYear entries carry ISO dates (e.g. "2026-01-01") while the
// budget columns work in years — split a date into its year and month.
const dateParts = (date) => {
  if (!date) return null;
  const [year, month] = String(date).split('-').map(Number);
  if (isNaN(year)) return null;
  return { year, month: month >= 1 && month <= 12 ? month : 1 };
};

// A period tag only applies to spans that sit EXACTLY on period boundaries —
// custom dates that merely fall inside a quarter are not "the quarter".
// Bounds are the "MM-DD" parts of the ISO dates.
const TRIMESTERS = [
  ['01-01', '03-31'],
  ['04-01', '06-30'],
  ['07-01', '09-30'],
  ['10-01', '12-31'],
];
const SEMESTERS = [
  ['01-01', '06-30'],
  ['07-01', '12-31'],
];

// Locations listed in the cell before the rest collapses into a "+N" tag.
const MAX_VISIBLE_LOCATIONS = 2;

// The rows of a page, as the store asks for them.
const PAGE_SIZE = 25;

// Default visible budget columns: the current year and the next two.
const defaultYears = () => {
  const currentYear = new Date().getFullYear();
  return [currentYear, currentYear + 1, currentYear + 2];
};

// Whole-calendar-year span: Jan 1 through Dec 31, same year or across years.
// These keep the classic display — the amount repeats on every covered year
// with the span underneath (e.g. "2026–2028") — instead of origin tags.
const isFullYearSpan = (cost) =>
  String(cost.startDate).slice(5) === '01-01' && String(cost.endDate || '').slice(5) === '12-31';

export default {
  name: 'PreProjectsDataTable',
  components: {
    BaseBottomSheet,
    BaseIcon,
    BaseInfoBanner,
    BasePhoneListControls,
    BasePullToRefresh,
    BaseRefreshButton,
    BaseRoundButton,
    BaseSheetChecklist,
    BaseTag,
    FormCheckbox,
    PreProjectsPhoneList,
    TableNormal,
    TablePagination,
    TableState,
  },
  props: {
    preProjects: {
      type: Array,
      default: () => [],
    },
    // Trial tenants get a first-page-only preview: sorting and pagination are
    // off so the visible page can't be steered to enumerate the dataset.
    trialRestricted: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      currentPage: 1,
      // Visible budget columns — the year filter drives them (see the
      // yearWindow watcher); the column toggle adjusts them manually.
      selectedYears: defaultYears(),
      yearsSheetOpen: false,
      phoneSortOpen: false,
    };
  },
  computed: {
    ...mapGetters('preProjects', [
      'pending',
      'page',
      'totalCount',
      'totalPages',
      'availableYears',
      'filters',
      'isSharePending',
      'loadingMore',
      'loadMoreError',
      'sortingField',
      'sortingOrder',
    ]),
    yearChoices() {
      return this.availableYears.map((year) => ({ id: year, name: String(year) }));
    },
    isPhone() {
      return viewport.size === 'phone';
    },
    isTouchScreen() {
      return !viewport.canHover;
    },
    // The stored year filter — watched to sync the budget columns.
    yearSelection() {
      const { years, fromYear, toYear } = this.filters || {};
      return { years: years || [], fromYear, toYear };
    },
    // Row id = the pre-project id used by the follow API; older payloads carry
    // no top-level id, so fall back to the first cost entry's preProjectId.
    items() {
      return this.preProjects.map((preProject, index) => ({
        ...preProject,
        id:
          preProject.id ||
          (preProject.costsByYear && preProject.costsByYear[0] && preProject.costsByYear[0].preProjectId) ||
          `pre-project-${index}`,
      }));
    },
    // One budget column per toggled-on year, in chronological order. The
    // toggle offers the search response's availableYears (whole result set).
    visibleYears() {
      return this.availableYears.filter((year) => this.selectedYears.includes(year));
    },
    headers() {
      // Sorting off on trial: re-sorting would rotate new real rows into the
      // visible preview.
      const sortable = !this.trialRestricted;
      return [
        {
          text: this.$t('page.pre-projects.data-table-titles.title'),
          value: 'title',
          sortable,
          width: '35%',
        },
        {
          text: this.$t('page.pre-projects.data-table-titles.emitter'),
          value: 'emitter',
          sortable,
        },
        {
          text: this.$t('page.pre-projects.data-table-titles.location'),
          // The backend's sort key for the first location's displayed name.
          value: 'city',
          sortable,
        },
        ...this.visibleYears.map((year) => ({
          text: this.$t('page.pre-projects.data-table-titles.budget', { year }),
          value: `budget-${year}`,
          sortable,
          align: 'right',
        })),
        { text: '', value: 'actions', sortable: false, align: 'right' },
      ];
    },
    hasResults() {
      return this.items.length > 0;
    },
    trialRemainingCount() {
      return Math.max(this.totalCount - this.items.length, 0);
    },
  },
  methods: {
    ...mapActions('preProjects', [
      'setSortingField',
      'setSortingOrder',
      'setContinuous',
      'follow',
      'unfollow',
      'share',
      'unshare',
    ]),
    formatLocalizedNumber,
    locationsOf,
    // The budget columns of a pre-project, for the phone list's rows and details.
    phoneBudgets(item) {
      return this.visibleYears.map((year) => {
        const exact = this.budgetExactEntry(item, year);
        return {
          year,
          amount: this.showsBudgetAmount(item, year) ? this.budgetLabel(item, year) : '',
          hasAmount: this.hasBudgetAmount(item, year),
          period: this.budgetPeriodLabel(item, year),
          exact: exact ? this.spanSentence(exact) : '',
          origins: this.budgetOrigins(item, year).map((origin) => origin.label),
          span: this.budgetSpanLabel(item, year),
        };
      });
    },
    // A place as the cell shows it: "Name · Region".
    locationLabel(location) {
      return [this.locationName(location), this.locationRegion(location)].filter(Boolean).join(' · ');
    },
    onPhoneSort({ field, order }) {
      this.setSortingField(field);
      this.setSortingOrder(order);
      this.$emit('page-changed', 1);
    },
    allSources(item) {
      return item.sources || [];
    },
    // ---- locations ----
    visibleLocations(item) {
      return locationsOf(item).slice(0, MAX_VISIBLE_LOCATIONS);
    },
    hiddenLocations(item) {
      return locationsOf(item).slice(MAX_VISIBLE_LOCATIONS);
    },
    // A custom place displays like a city; otherwise the catalog name.
    locationName(location) {
      return locationDisplayName(location);
    },
    // The region tag — shown even when it repeats the city name (Halifax is a
    // city in the Halifax region); skipped only when the entry IS the region.
    locationRegion(location) {
      if (!location.regionName) return null;
      if (location.level === LOCATION_LEVEL.REGION && !location.customName) return null;
      return location.regionName;
    },
    // The province name, shown on hover only — skipped when the entry IS the province.
    locationProvince(location) {
      if (!location.provinceName) return null;
      if (location.level === LOCATION_LEVEL.PROVINCE && !location.customName) return null;
      return location.provinceName;
    },
    // One line per collapsed location: "Name · Region, Province" — the name as the
    // rows show it, then where it is (HTML — v-tooltip renders it). Custom place
    // names are user-entered (form and CSV import), so every part is escaped
    // before it joins the markup.
    hiddenLocationsTooltip(item) {
      return this.hiddenLocations(item)
        .map((location) => {
          const where = [this.locationRegion(location), this.locationProvince(location)]
            .filter(Boolean)
            .map(escapeHtml)
            .join(', ');
          return [escapeHtml(this.locationName(location)), where].filter(Boolean).join(' · ');
        })
        .join('<br>');
    },
    // Open a source: a LINK opens its external URL directly; a DOCUMENT
    // resolves its presigned URL first, then navigates to it
    openSource(item, source) {
      if (source.type === 'LINK') {
        window.open(source.url, '_blank', 'noopener');
        return;
      }
      preProjectSourcesService
        .getDownloadUrl(item.id, source.id)
        .then((url) => window.open(url, '_blank', 'noopener'))
        .catch((error) => this.$toast.error(error));
    },
    // Follow/unfollow with an undo toast, mirroring the tenders bookmark action.
    // The toast plugin does not observe the undo callback's promise, so each
    // undo reports its own failure.
    onFollowClick(item) {
      const id = item.id;
      if (item.followed) {
        this.unfollow(id)
          .then(() =>
            this.$toast.cancel(this.$t('page.pre-projects.unfollow-toast'), () =>
              // On the watchlist view the row was removed — refetch so it returns.
              this.follow(id)
                .then(() => this.$emit('refresh'))
                .catch((error) => this.$toast.error(error)),
            ),
          )
          .catch((error) => this.$toast.error(error));
      } else {
        this.follow(id)
          .then(() =>
            this.$toast.cancel(this.$t('page.pre-projects.follow-toast'), () =>
              this.unfollow(id).catch((error) => this.$toast.error(error)),
            ),
          )
          .catch((error) => this.$toast.error(error));
      }
    },
    // Share/unshare with the whole tenant, with an undo toast, mirroring the tenders share action.
    // One request per row at a time: while one is in flight (button disabled,
    // but a queued click can still land) a second click would dispatch the
    // opposite operation and the replies could settle out of order.
    onShareClick(item) {
      const id = item.id;
      if (this.isSharePending(id)) return;
      if (item.shared) {
        this.$track.preProjectUnshare('pre_projects_table');
        this.unshare(id)
          .then(() =>
            this.$toast.cancel(this.$t('page.pre-projects.unshare-toast'), () =>
              // On the shared view the row was removed — refetch so it returns.
              this.share(id)
                .then(() => this.$emit('refresh'))
                .catch((error) => this.$toast.error(error)),
            ),
          )
          .catch((error) => this.$toast.error(error));
      } else {
        this.$track.preProjectShare('pre_projects_table');
        this.share(id)
          .then(() =>
            this.$toast.cancel(this.$t('page.pre-projects.share-toast'), () =>
              this.unshare(id).catch((error) => this.$toast.error(error)),
            ),
          )
          .catch((error) => this.$toast.error(error));
      }
    },
    // The cost entries covering a given year, dates parsed. Multiple entries
    // can cover the same year when a multi-year budget overlaps a single-year one.
    budgetEntriesFor(item, year) {
      return (item.costsByYear || [])
        .map((cost) => ({ ...cost, start: dateParts(cost.startDate), end: dateParts(cost.endDate) }))
        .filter((cost) => cost.start && cost.start.year <= year && year <= (cost.end || cost.start).year);
    },
    // Amounts belong to the year a budget starts — later covered years only
    // reference it (budgetOriginLabels).
    startingEntriesFor(item, year) {
      return this.budgetEntriesFor(item, year).filter((cost) => cost.start.year === year);
    },
    // The entries whose amount belongs in a column: those starting that year,
    // plus whole-year spans, which repeat their amount on every covered year.
    amountEntriesFor(item, year) {
      return this.budgetEntriesFor(item, year).filter((cost) => cost.start.year === year || isFullYearSpan(cost));
    },
    // The aggregated amount of a column's entries.
    budgetFor(item, year) {
      const entries = this.amountEntriesFor(item, year);
      if (!entries.length) return null;
      // Entries with no cost at all mean "not specified" (N/A). On priced
      // entries a null bound is open-ended: no max => "≥ min", no min => "≤ max".
      const priced = entries.filter((cost) => cost.expectedCostMin != null || cost.expectedCostMax != null);
      const minUnbounded = priced.length > 0 && priced.every((cost) => cost.expectedCostMin == null);
      const maxUnbounded = priced.some((cost) => cost.expectedCostMax == null);
      return {
        hasAmount: priced.length > 0,
        min: minUnbounded ? null : priced.reduce((sum, cost) => sum + (cost.expectedCostMin || 0), 0),
        max: maxUnbounded ? null : priced.reduce((sum, cost) => sum + (cost.expectedCostMax || 0), 0),
      };
    },
    // Sub-year period an entry covers within its start year — only when the
    // dates align exactly to period bounds, quarters first: one exact quarter
    // → { trimester, 1–4 }; a span over several exact quarters →
    // { trimester-range, from, to } (e.g. "Q1 - Q2" — the quarter reading
    // beats the semester one). Multi-year spans read as a semester, never a
    // quarter. Full years and custom ranges carry no period.
    periodOf(cost) {
      const startBound = String(cost.startDate).slice(5);
      const sameYear = !cost.end || cost.end.year === cost.start.year;
      if (!sameYear) {
        // The start-year portion (clamped to Dec 31) must sit inside one
        // semester and start on a quarter boundary: a Jul 1 or Oct 1 start →
        // S2. Anything else is custom dates (whole-years spans stay unmarked
        // — see isFullYearSpan).
        const startsOnQuarter = TRIMESTERS.some(([start]) => start === startBound);
        return startsOnQuarter && startBound >= SEMESTERS[1][0] ? { type: 'semester', index: 2 } : null;
      }
      const endBound = String(cost.endDate || cost.startDate).slice(5);
      const from = TRIMESTERS.findIndex(([start]) => start === startBound);
      const to = TRIMESTERS.findIndex(([, end]) => end === endBound);
      if (from !== -1 && from === to) return { type: 'trimester', index: from + 1 };
      if (from !== -1 && to !== -1 && from < to && !(from === 0 && to === 3)) {
        return { type: 'trimester-range', from: from + 1, to: to + 1 };
      }
      return null;
    },
    // The tag text for a period — localized, so a trimester reads "Q2" in
    // English and "T2" in French; a range reads "Q1 - Q2".
    periodShortLabel(period) {
      if (period.type === 'trimester-range') {
        const from = this.$t('page.pre-projects.period.trimester-short', { index: period.from });
        const to = this.$t('page.pre-projects.period.trimester-short', { index: period.to });
        return `${from} - ${to}`;
      }
      return this.$t(`page.pre-projects.period.${period.type}-short`, { index: period.index });
    },
    // The full period name for tooltips ("Quarter 1 - Quarter 2").
    periodTooltipName(period) {
      if (period.type === 'trimester-range') {
        const from = this.$t('page.pre-projects.period.trimester', { index: period.from });
        const to = this.$t('page.pre-projects.period.trimester', { index: period.to });
        return `${from} - ${to}`;
      }
      return this.$t(`page.pre-projects.period.${period.type}`, { index: period.index });
    },
    // The shared period of the entries starting a given year — only when they
    // all agree on it (aggregated amounts can't carry a single period).
    budgetPeriod(item, year) {
      const periods = this.startingEntriesFor(item, year).map((cost) => this.periodOf(cost));
      const period = periods[0];
      const samePeriod = (p) =>
        p && p.type === period.type && p.index === period.index && p.from === period.from && p.to === period.to;
      return period && periods.every(samePeriod) ? period : null;
    },
    budgetPeriodLabel(item, year) {
      const period = this.budgetPeriod(item, year);
      return period ? this.periodShortLabel(period) : null;
    },
    // The whole-years span shown under the amount (e.g. "2026–2028") when a
    // full-year budget covers more than one year.
    budgetSpanLabel(item, year) {
      const spans = this.amountEntriesFor(item, year).filter(
        (cost) => isFullYearSpan(cost) && cost.end && cost.end.year > cost.start.year,
      );
      if (!spans.length) return null;
      const from = Math.min(...spans.map((cost) => cost.start.year));
      const to = Math.max(...spans.map((cost) => cost.end.year));
      return `${from}–${to}`;
    },
    // A custom span — not whole years, not an exact quarter/semester — is
    // marked by an "exact dates" chip; its tooltip spells the range out.
    budgetExactEntry(item, year) {
      return this.startingEntriesFor(item, year).find((cost) => !isFullYearSpan(cost) && !this.periodOf(cost)) || null;
    },
    budgetExactTooltip(item, year) {
      const entry = this.budgetExactEntry(item, year);
      if (!entry) return null;
      return `<strong>${this.$t('page.pre-projects.period.exact-dates')}</strong><br>${this.spanSentence(entry)}`;
    },
    // Later years covered by a multi-year budget don't repeat the amount — they
    // reference where it starts instead: "S2-2026", or just "2026" without a
    // period. The tooltip carries the exact span and points back at that
    // year's column for the cost.
    budgetOrigins(item, year) {
      const origins = new Map();
      this.budgetEntriesFor(item, year)
        // Whole-year spans repeat their amount instead of referencing back.
        .filter((cost) => cost.start.year < year && !isFullYearSpan(cost))
        .forEach((cost) => {
          const period = this.periodOf(cost);
          const label = period ? `${this.periodShortLabel(period)}-${cost.start.year}` : `${cost.start.year}`;
          if (origins.has(label)) return;
          // The tooltip is HTML (v-tooltip renders it): bold period name, one
          // line per part.
          const name = period ? `<strong>${this.periodTooltipName(period)} ${cost.start.year}</strong>` : null;
          const tooltip = [
            name,
            this.spanSentence(cost),
            this.$t('page.pre-projects.period.origin-cost', { year: cost.start.year }),
          ]
            .filter(Boolean)
            .join('<br>');
          origins.set(label, { label, tooltip });
        });
      return [...origins.values()];
    },
    // The amount span shows when a budget starts that year, or when nothing
    // covers it at all (the N/A state) — origin-only cells show just their tag.
    showsBudgetAmount(item, year) {
      return !!this.budgetFor(item, year) || !this.budgetOrigins(item, year).length;
    },
    // Tooltip of the start-year period tag. A same-year quarter names itself;
    // ranges, semesters and multi-year spans also spell out the exact dates
    // on their own line under the bolded period name (HTML — v-tooltip
    // renders it).
    budgetPeriodTooltip(item, year) {
      const period = this.budgetPeriod(item, year);
      if (!period) return null;
      const name = this.periodTooltipName(period);
      const entry = this.startingEntriesFor(item, year)[0];
      const crossesYears = !!(entry && entry.end && entry.end.year > entry.start.year);
      if (period.type === 'trimester' && !crossesYears) return name;
      return entry ? `<strong>${name}</strong><br>${this.spanSentence(entry)}` : `<strong>${name}</strong>`;
    },
    // The exact span a budget covers, in the app's standard date format (YYYY-MM-DD).
    spanSentence(cost) {
      return this.$t('page.pre-projects.period.spans', {
        start: formatStandardDate(cost.startDate),
        end: formatStandardDate(cost.endDate || cost.startDate),
      });
    },
    hasBudgetAmount(item, year) {
      const budget = this.budgetFor(item, year);
      return !!(budget && budget.hasAmount);
    },
    // The cell text for any (item, year): N/A when there's no displayable
    // amount (no entry, or null costs), a single amount when min equals max,
    // otherwise the range.
    budgetLabel(item, year) {
      const budget = this.budgetFor(item, year);
      if (!budget || !budget.hasAmount) return 'N/A';
      // Open-ended bounds: no max => at least min, no min => at most max.
      if (budget.min != null && budget.max == null) return `> ${formatNumToCurrency(budget.min)}`;
      if (budget.min == null && budget.max != null) return `< ${formatNumToCurrency(budget.max)}`;
      if (budget.min == null && budget.max == null) return 'N/A';
      if (budget.min === budget.max) return formatNumToCurrency(budget.min);
      return `${formatNumToCurrency(budget.min)} – ${formatNumToCurrency(budget.max)}`;
    },
    isNewPreProject,
    hasProjectNumber,
    openYearsSheet() {
      if (this.isPhone) this.yearsSheetOpen = true;
    },
    toggleYear(year) {
      const index = this.selectedYears.indexOf(year);
      if (index >= 0) this.selectedYears.splice(index, 1);
      else this.selectedYears.push(year);
    },
    onInputPage(page) {
      this.$emit('page-changed', page);
    },
    // Server-side sorting (mirrors TendersDataTable): store the sort, then
    // re-fetch from page 1. An unsorted third click falls back to the default.
    onSortBy(field) {
      // Unsorted (third header click) falls back to the default: newest first.
      const column = (Array.isArray(field) ? field[0] : field) || 'createdAt';
      // A budget column sorts by that year's cost: "budget-2027" → "cost:2027".
      const sortingField = column.startsWith('budget-') ? `cost:${column.slice('budget-'.length)}` : column;
      this.setSortingField(sortingField);
      this.$emit('page-changed', 1);
    },
    onSortDesc(order) {
      const isDescending = Array.isArray(order) ? order[0] : order;
      this.setSortingOrder(getSortingOrder(!!isDescending));
      this.$emit('page-changed', 1);
    },
  },
  watch: {
    page() {
      this.currentPage = this.page;
    },
    isPhone: {
      immediate: true,
      handler(isPhone, wasPhone) {
        this.setContinuous(isPhone);
        if (wasPhone === undefined) return;
        // Crossing the phone width (a rotation, a resized window): the phone list grows from
        // page 1 and the table shows one page, so reload what the new layout expects.
        const pagesListed = this.preProjects.length > PAGE_SIZE;
        if ((isPhone && this.page > 1) || (!isPhone && pagesListed)) this.$emit('page-changed', 1);
      },
    },
    // Toggling the year filter surfaces exactly the picked years as budget
    // columns (older window-only filters expand to their range); clearing it
    // restores the default window. Other filter changes leave the user's
    // manual column picks alone.
    yearSelection(next, prev) {
      if (prev && JSON.stringify(next) === JSON.stringify(prev)) return;
      if (next.years.length) {
        this.selectedYears = [...next.years].sort((a, b) => a - b);
        return;
      }
      if (next.fromYear == null && next.toYear == null) {
        this.selectedYears = defaultYears();
        return;
      }
      const from = next.fromYear != null ? next.fromYear : next.toYear;
      const to = next.toYear != null ? next.toYear : next.fromYear;
      this.selectedYears = Array.from({ length: to - from + 1 }, (_, offset) => from + offset);
    },
  },
  mounted() {
    this.currentPage = this.page;
  },
};
</script>

<style lang="scss">
.pre-projects-data-table {
  display: flex;
  flex: auto;
  flex-direction: column;

  // Rows aren't clickable (no row navigation) — undo TableNormal's pointer cursor.
  .v-data-table tbody {
    cursor: default;
  }

  .v-data-table.theme--light {
    border-radius: $border-radius-root;

    table {
      border-collapse: separate;
      position: relative;
      background: $color-white;
      border-radius: $border-radius-root;
    }

    thead th {
      background-color: $color-white;
      border-bottom: 0.5px solid $color-border !important;

      &:first-child {
        border-radius: $border-radius-root 0 0 0;
      }

      &:last-child {
        border-radius: 0 $border-radius-root 0 0;
      }
    }
  }

  &__results-section {
    display: flex;
    align-items: center;
    // The same room before the list as on the other lists.
    padding-bottom: $space-3;
    justify-content: space-between;
  }

  &__buttons {
    display: flex;
    gap: 8px;
  }

  &__columns-menu {
    background: $color-white;
    border-radius: $border-radius-root;
    display: flex;
    flex-direction: column;
    min-width: 180px;
    padding: 8px;
  }

  &__columns-menu-heading {
    color: $color-neutral;
    font-size: $font-size-xsmall;
    font-weight: 600;
    letter-spacing: 0.04em;
    padding: 4px 8px;
    text-transform: uppercase;
  }

  &__count {
    color: $color-neutral;
    font-size: $font-size-small;
    padding: 4px 12px;
    text-transform: uppercase;
  }

  &__content {
    flex: auto;
    margin-bottom: 96px;
    position: relative;
  }

  &__trial-end-banner {
    margin-top: 12px;
  }

  &__pagination {
    position: fixed;
    bottom: 32px;
    left: 50%;
    transform: translateX(-50%);
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
  }

  &__row-title-container {
    display: flex;
    flex-direction: column;
  }

  &__row-title {
    font-weight: 600;
    color: $color-normal;
    margin-left: $space-sm;
  }

  // Trails the title text, flowing inline with its last word.
  &__new-tag {
    display: inline-flex;
    vertical-align: middle;
    margin-left: 4px;
  }

  &__row-subtitle {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-top: 4px;
    color: $color-dark-blue-pale;
  }

  // The source tag opens the source (single) or reveals the popover (hover).
  &__sources-tag {
    cursor: pointer;
  }

  // The single-source open affordance, trailing the "Source" text.
  &__sources-tag-ico {
    width: 12px;
    height: 12px;
    margin-left: 4px;
    vertical-align: -1px;
  }

  // Sources popover: one clickable row per attached link/document.
  &__sources {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: $space-1;
    max-width: 280px;
  }

  &__source {
    display: flex;
    align-items: center;
    gap: $space-2;
    width: 100%;
    padding: 4px 6px;
    border: none;
    border-radius: $border-radius-small;
    background: none;
    text-align: left;
    cursor: pointer;

    &:hover {
      background: $color-slate-100;

      .pre-projects-data-table__source-open {
        opacity: 1;
      }
    }
  }

  &__source-ico {
    flex: none;
    width: 14px;
    height: 14px;
    color: $color-slate-500;
  }

  &__source-name {
    flex: 1;
    min-width: 0;
    font-size: $font-size-small;
    color: $color-normal;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  // The open affordance — revealed on row hover.
  &__source-open {
    flex: none;
    width: 14px;
    height: 14px;
    color: $color-slate-500;
    opacity: 0;
    transition: opacity 150ms ease;

    @include no-hover {
      opacity: 1;
    }
  }

  &__emitter {
    font-size: $font-size-small;
    color: $color-normal;
  }

  &__empty-cell {
    color: $color-neutral;
  }

  &__locations {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
  }

  // One line per place: the name, then its region muted. Wraps only when the
  // column is too narrow for both.
  &__location {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    column-gap: 4px;
  }

  &__location-name {
    font-weight: 600;
    color: $color-normal;
  }

  &__location-region {
    color: $color-slate-500;
    white-space: nowrap;
  }

  &__locations-more {
    margin-top: 4px;
  }

  &__budget {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 2px;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  &__budget-main {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 6px;
  }

  // The whole-years span under the amount (e.g. "2026–2028").
  &__budget-span {
    font-size: $font-size-xsmall;
    color: $color-slate-500;
  }

  &__budget-amount {
    font-weight: 600;
    color: $color-normal;

    &.is-na {
      font-weight: 400;
      color: $color-neutral;
    }
  }
}
</style>
